import Dropdown, { DropdownItem } from '@/components/Dropdown';
import { getClasses, onEnter } from '@/utils';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Button } from 'react-bootstrap';
import { DISCOUNT_LINE_ITEM_IDS } from '@/constants';
import EditableCell from '@/common/Table/cells/EditableCell';
import Input from '@/components/Input';
import { InvoiceCustomCharge } from '@/models/gen/graphql';
import Table from '@/common/Table';
import Tippy from '@tippyjs/react';
import prompt from '@/common/Popup/prompt';
import { useGetLineItemTypes } from '@/api/services/filters/lineItemTypes';

export type CustomInvoiceTableRowProps = InvoiceCustomCharge & {
  onDelete?: () => Promise<void>;
  preview?: any;
  setPreview?: (preview: any) => void;
};

const CustomInvoiceTableRow = ({ id, index, type, note, cost, onDelete, preview, setPreview }: CustomInvoiceTableRowProps): JSX.Element => {
  const { customCharges = [] } = preview || {};
  const [{ data: lineItemTypes = [] }, { fetch: getLineItemTypes }] = useGetLineItemTypes();

  useEffect((): void => {
    getLineItemTypes();
  }, []);

  const onChange =
    (field: keyof InvoiceCustomCharge): ((value: string | number) => void) =>
    (value: string | number): void => {
      if (field === 'cost' && typeof value === 'string') value = parseFloat(value.replace(/[^0-9.-]+/g, '')) || 0;
      if (customCharges?.find((charge) => charge.id === id)?.[field] === value) return;
      setPreview?.((current) => {
        const result = {
          ...current,
          customCharges: current.customCharges.map(
            (charge: InvoiceCustomCharge): InvoiceCustomCharge => (id === charge.id ? { ...charge, [field]: value } : charge)
          ),
        };
        return result;
      });
    };

  const items = useMemo(() => {
    const result: DropdownItem[] = lineItemTypes
      .filter(({ id }) => !DISCOUNT_LINE_ITEM_IDS.includes(id))
      .map(({ displayName }) => ({
        value: displayName,
        label: displayName,
      }));
    customCharges
      .sort((a, b) => (a.type || '').localeCompare(b.type || ''))
      .forEach((charge) => {
        if (!!charge.type && !result.find((item) => item.value === charge.type)) {
          result.unshift({ value: charge.type, label: charge.type });
        }
      });
    result.unshift({ value: '+', label: 'Custom Line Item', icon: <i className="fa fa-plus" /> });
    return result;
  }, [lineItemTypes, type, customCharges]);
  const TypeDropdown = useMemo(
    () => (props) => (
      <Dropdown
        {...props}
        className="item-type-dropdown"
        onChange={(value) => {
          if (value !== '+') return props.onChange(value);
          prompt('Enter the name for your new custom item type:', props.value || '', 'Enter New Item Type')
            .then(props.onChange)
            .catch(() => {
              props.onChange(props.value);
            });
        }}
        items={items}
        options={{ showClearButton: false, showChevron: false, locale: { 'Select...': '--' } }}
      />
    ),
    [items]
  );
  const NotesInput = useMemo(() => (props) => <Input {...props} placeholder="--" onFocus={(event) => event.target.select()} />, []);

  return (
    <Table.Row id={id} className={getClasses(note === 'Subtotal' ? 'SUBTOTAL' : '')}>
      <Table.Cell className="DELETE">
        <Tippy content="Delete">
          <Button name="DELETE_LINE_ITEM" className="p-0" variant="icon" onClick={onDelete} tabIndex={-1}>
            <i className="fa fa-trash text-danger" />
          </Button>
        </Tippy>
      </Table.Cell>
      <Table.Cell className="INDEX">{index}</Table.Cell>
      <EditableCell className="ITEM_TYPE" as={TypeDropdown} value={type} onChange={onChange('type')} />
      <EditableCell className="NOTES" disabled={note === 'Subtotal'} as={NotesInput} value={note} onChange={onChange('note')} />
      <EditableCell
        className="COST"
        disabled={note === 'Subtotal'}
        as={StaticCurrencyInput}
        value={note === 'Subtotal' ? formatCurrency.USD(cost) : cost}
        onChange={onChange('cost')}
      />
    </Table.Row>
  );
};

const StaticCurrencyInput = (props) => {
  const [active, setActive] = useState(false);
  const [value, setValue] = useState(parseFloat(props.value).toFixed(2));
  const ref = useRef(null);

  return (
    <Input
      {...props}
      type="text"
      value={active ? value : formatCurrency.USD(value)}
      ref={ref}
      onChange={(val) => {
        setValue(val.replace(/[^0-9.-]+/g, ''));
      }}
      onBlur={() => {
        setActive(false);
        const val = parseFloat(value || '0').toFixed(2);
        props.onChange(val);
        setValue(val);
      }}
      onFocus={(event) => {
        setActive(true);
        const timer = setTimeout(() => {
          event.target.select();
          clearTimeout(timer);
        }, 100);
      }}
      onKeyDown={onEnter(() => {
        ref.current.blur();
      })}
      style={{
        textAlign: 'right',
      }}
    />
  );
};

const formatCurrency = {
  USD: (value) => {
    value = `${value}`;
    const result = Intl.NumberFormat('en-US', {
      currency: 'USD',
      style: 'currency',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(parseFloat(value.replace(/[^0-9.-]+/g, '').trim()));
    return result;
  },
};

export default CustomInvoiceTableRow;
